import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as cartApi from '@/api/cart';
import { cartKeys } from '@/api/cart';
import { ApiException } from '@/types/api';
import type { AddToCartPayload, Cart } from '@/types/cart';
import { usePlatform } from '@/providers/PlatformProvider';

export interface CartContextValue {
  cart: Cart | null;
  itemCount: number;
  isLoading: boolean;
  isMutating: boolean;

  /** Drawer visibility — §63. */
  isOpen: boolean;
  open: () => void;
  close: () => void;

  add: (payload: AddToCartPayload) => Promise<void>;
  updateQuantity: (itemId: number, quantity: number) => Promise<void>;
  remove: (itemId: number) => Promise<void>;
  clear: () => Promise<void>;

  /**
   * Re-read the cart from the server.
   *
   * For when something *else* changed it — placing an order empties the
   * cart server-side, and without this the header badge and drawer would
   * keep showing items the customer has already bought. Distinct from
   * `clear()`, which asks the server to empty it.
   */
  refresh: () => Promise<void>;
}

export const CartContext = createContext<CartContextValue | null>(null);

/**
 * Cart state — BUILD-PLAN §13, §63.
 *
 * The server owns the cart. Every mutation returns the whole updated cart and
 * that response replaces the cache, so the client never derives totals or
 * patches its own copy — the figure on screen is always the one the backend
 * calculated (§13/§33).
 *
 * That also rules out optimistic updates here: showing an item as added
 * before the server has accepted it would mean showing a basket that may be
 * rejected for stock or personalisation a moment later.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isEnabled } = usePlatform();
  const [isOpen, setIsOpen] = useState(false);

  const cartEnabled = isEnabled('cart');

  const { data, isPending } = useQuery({
    queryKey: cartKeys.cart,
    queryFn: cartApi.fetchCart,
    // Never call the endpoint when the feature is off — it would 503 and
    // surface an error for something the customer cannot use anyway.
    enabled: cartEnabled,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiException && error.isComingSoon) return false;

      return failureCount < 1;
    },
  });

  /**
   * Every mutation returns the authoritative cart; adopt it wholesale.
   *
   * The delivery estimate is invalidated at the same time, and that is not
   * optional. It is keyed on destination, method and discount code — NOT on
   * the cart — so changing a quantity left the previous total on screen until
   * something else happened to refetch it. The customer saw a basket of two
   * priced as a basket of three, which is the kind of wrong figure that
   * destroys trust in every other number on the page (§33, §86).
   *
   * Keying the estimate on the cart's contents instead would work, but this
   * is the honest description of what happened: the cart changed, so the
   * quote derived from it is stale.
   */
  const adopt = useCallback(
    (cart: Cart) => {
      queryClient.setQueryData(cartKeys.cart, cart);
      void queryClient.invalidateQueries({ queryKey: ['cart', 'estimate'] });
    },
    [queryClient],
  );

  const addMutation = useMutation({ mutationFn: cartApi.addToCart, onSuccess: adopt });

  const updateMutation = useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: number; quantity: number }) =>
      cartApi.updateCartItem(itemId, quantity),
    onSuccess: adopt,
  });

  const removeMutation = useMutation({ mutationFn: cartApi.removeCartItem, onSuccess: adopt });
  const clearMutation = useMutation({ mutationFn: cartApi.clearCart, onSuccess: adopt });

  const value = useMemo<CartContextValue>(
    () => ({
      cart: data ?? null,
      itemCount: data?.itemCount ?? 0,
      isLoading: cartEnabled && isPending,
      isMutating:
        addMutation.isPending ||
        updateMutation.isPending ||
        removeMutation.isPending ||
        clearMutation.isPending,

      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),

      add: async (payload) => {
        await addMutation.mutateAsync(payload);
        // Opening on success rather than on click: the drawer only appears
        // once there is genuinely something new in it.
        setIsOpen(true);
      },
      updateQuantity: async (itemId, quantity) => {
        await updateMutation.mutateAsync({ itemId, quantity });
      },
      remove: async (itemId) => {
        await removeMutation.mutateAsync(itemId);
      },
      clear: async () => {
        await clearMutation.mutateAsync();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: cartKeys.cart });
      },
    }),
    [
      data,
      cartEnabled,
      isPending,
      isOpen,
      addMutation,
      updateMutation,
      removeMutation,
      clearMutation,
      queryClient,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used within a CartProvider.');
  }

  return context;
}
